import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nProvider";
import { useAuth } from "@/lib/AuthContext";
import { DEAD } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Bell, Check, Trash2, Upload, Paperclip, FileText, Download, Pencil, Mail, ScanLine, Search, RefreshCw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function daysBetween(iso) {
  if (!iso) return null;
  const d = new Date(iso.slice(0, 10));
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / (1000 * 60 * 60 * 24));
}

function addYearsIso(iso, y) {
  const d = new Date(iso);
  d.setFullYear(d.getFullYear() + y);
  return d.toISOString().slice(0, 10);
}
function addMonthsIso(iso, m) {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + m);
  return d.toISOString().slice(0, 10);
}
function suggestDueDate(base_date, subtypeMeta, option_years) {
  if (!base_date || !subtypeMeta) return "";
  const v = subtypeMeta.validity || {};
  if (v.manual) return "";
  if (v.options) {
    const y = option_years || v.options[0].years;
    return addYearsIso(base_date, y);
  }
  if (v.years) return addYearsIso(base_date, v.years);
  if (v.months) return addMonthsIso(base_date, v.months);
  return "";
}

const EMPTY = {
  company_id: "", worker_id: "", category: "formazione",
  subtype: "", subtype_option_years: null,
  title: "", notes: "", base_date: "", due_date: "",
};

export default function DeadlinesPage() {
  const { t, lang } = useI18n();
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === "super_admin";
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [list, setList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [catalog, setCatalog] = useState({ categories: [], subtypes: [] });
  const [scheduler, setScheduler] = useState(null);
  const [fCategory, setFCategory] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fSearch, setFSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pendingFile, setPendingFile] = useState(null);
  const fileRef = useRef(null);
  const attachRefs = useRef({});

  const filteredFormWorkers = useMemo(
    () => workers.filter((w) => !form.company_id || w.company_id === form.company_id),
    [workers, form.company_id]
  );

  const load = useCallback(() => api.get("/deadlines").then((r) => setList(r.data)), []);
  useEffect(() => {
    load();
    api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/workers").then((r) => setWorkers(r.data));
    api.get("/deadlines/catalog").then((r) => setCatalog(r.data));
    api.get("/deadlines/scheduler/status").then((r) => setScheduler(r.data)).catch(() => {});
  }, [load]);

  const subtypesForCategory = catalog.subtypes.filter((s) => s.category === form.category);
  const currentSubtype = catalog.subtypes.find((s) => s.key === form.subtype);
  const hasOptions = currentSubtype?.validity?.options?.length > 0;
  const isManual = currentSubtype?.validity?.manual === true;

  // Auto-suggest due_date when subtype/base_date/option changes AND user hasn't manually overridden
  useEffect(() => {
    if (!currentSubtype) return;
    const suggested = suggestDueDate(form.base_date, currentSubtype, form.subtype_option_years);
    if (suggested && (!form.due_date || form.due_date === form._prev_suggested)) {
      setForm((f) => ({ ...f, due_date: suggested, _prev_suggested: suggested }));
    }
    // eslint-disable-next-line
  }, [form.base_date, form.subtype, form.subtype_option_years]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        company_id: form.company_id,
        worker_id: form.worker_id || null,
        category: form.category,
        subtype: form.subtype || null,
        subtype_option_years: form.subtype_option_years || null,
        title: form.title,
        notes: form.notes || null,
        base_date: form.base_date || null,
        due_date: form.due_date || null,
      };
      const r = editingId
        ? await api.put(`/deadlines/${editingId}`, payload)
        : await api.post("/deadlines", payload);
      const did = editingId || r.data.id;
      if (pendingFile) {
        const fd = new FormData();
        fd.append("file", pendingFile);
        await api.post(`/deadlines/${did}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY);
      setPendingFile(null);
      toast.success(t("save"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const startEdit = (d) => {
    setEditingId(d.id);
    setForm({
      company_id: d.company_id || "",
      worker_id: d.worker_id || "",
      category: d.category || "formazione",
      subtype: d.subtype || "",
      subtype_option_years: d.subtype_option_years || null,
      title: d.title || "",
      notes: d.notes || d.description || "",
      base_date: d.base_date ? d.base_date.slice(0, 10) : "",
      due_date: d.due_date ? d.due_date.slice(0, 10) : "",
      _prev_suggested: null,
    });
    setPendingFile(null);
    setOpen(true);
  };

  const complete = async (id) => { await api.post(`/deadlines/${id}/complete`); load(); };
  const remove = async (id) => {
    if (!window.confirm(t("delete") + "?")) return;
    await api.delete(`/deadlines/${id}`);
    load();
  };

  const uploadAttachment = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/deadlines/${id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(lang === "it" ? "Allegato caricato" : "Attachment uploaded");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const removeAttachment = async (id) => {
    if (!window.confirm(lang === "it" ? "Rimuovere l'allegato?" : "Remove attachment?")) return;
    await api.delete(`/deadlines/${id}/attachment`);
    load();
  };

  const downloadAttachment = async (d) => {
    if (!d.attachment_url) return;
    try {
      const r = await api.get(d.attachment_url.replace("/api", ""), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.attachment_filename || `allegato-${d.id}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(`${BACKEND}${d.attachment_url}`, "_blank");
    }
  };

  const runReminders = async () => {
    try {
      const r = await api.post("/deadlines/run-reminders");
      toast.success(`${t("reminders_sent")}: ${r.data.sent}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const runBackfillGenerale = async () => {
    if (backfillRunning) return;
    try {
      // 1. Dry-run per anteprima
      const dry = await api.post("/deadlines/backfill-generale?dry_run=true");
      const d0 = dry.data || {};
      const toG = d0.migrated_to_generale || 0;
      const toC = d0.migrated_to_combinato || 0;
      const sk = d0.skipped || 0;
      const sc = d0.scanned || 0;
      if (toG === 0 && toC === 0) {
        toast.info(`Nessuna scadenza da migrare (scansionate ${sc}, tutte già corrette).`);
        return;
      }
      const ok = window.confirm(
        `Migrazione Formazione Generale — anteprima:\n\n` +
        `• Scansionate: ${sc}\n` +
        `• Da migrare a PARTE GENERALE (senza scadenza): ${toG}\n` +
        `• Da migrare a GENERALE + SPECIFICA (5 anni): ${toC}\n` +
        `• Già corrette (specifica pura): ${sk}\n\n` +
        `Vuoi applicare la migrazione? L'operazione è idempotente.`
      );
      if (!ok) return;
      setBackfillRunning(true);
      const r = await api.post("/deadlines/backfill-generale");
      const d = r.data || {};
      toast.success(
        `Migrazione completata: ${d.migrated_to_generale} → Parte Generale, ${d.migrated_to_combinato} → Generale+Specifica.`
      );
      if (d.errors?.length) {
        toast.warning(`${d.errors.length} errori — vedi console`);
        console.warn("Backfill errors:", d.errors);
      }
      // Ricarica lista
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore migrazione");
    }
    setBackfillRunning(false);
  };

  // Ricerca full-text su titolo, sottotipo, nome/cognome/CF del lavoratore, azienda
  const workerById = new Map(workers.map((w) => [w.id, w]));
  const companyById = new Map(companies.map((c) => [c.id, c]));
  const _q = fSearch.trim().toLowerCase();
  const filtered = list.filter((d) => {
    if (fCategory !== "all" && d.category !== fCategory) return false;
    if (fStatus !== "all" && d.status !== fStatus) return false;
    if (!_q) return true;
    const w = d.worker_id ? workerById.get(d.worker_id) : null;
    const c = d.company_id ? companyById.get(d.company_id) : null;
    const hay = [
      d.title, d.subtype, d.subtype_label,
      w?.first_name, w?.last_name, w?.tax_code, w?.email, w?.role,
      c?.name, c?.vat_number, c?.tax_code,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(_q);
  });

  const catLabels = { formazione: t("formazione"), documentale: t("documentale"), impianti: t("impianti"), sanitaria: t("sanitaria"), analisi: t("analisi"), consulenza: t("consulenza") };

  return (
    <div data-testid={DEAD.root} className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("deadlines")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{filtered.length} / {list.length}</p>
          {scheduler?.running && scheduler.jobs?.[0]?.next_run_time && (
            <p className="text-[11px] text-muted-foreground mt-1 inline-flex items-center gap-1" data-testid="scheduler-status">
              <Bell className="w-3 h-3" />
              {lang === "it" ? "Promemoria automatici attivi — prossimo invio: " : "Auto reminders active — next run: "}
              {new Date(scheduler.jobs[0].next_run_time).toLocaleString(lang === "it" ? "it-IT" : "en-US", { dateStyle: "short", timeStyle: "short" })}
            </p>
          )}
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              value={fSearch}
              onChange={(e) => setFSearch(e.target.value)}
              placeholder={lang === "it" ? "Cerca per lavoratore, azienda, titolo…" : "Search by worker, company, title…"}
              className="pl-9 w-64 h-10"
              data-testid="deadlines-search-input"
            />
            {fSearch && (
              <button
                type="button"
                onClick={() => setFSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs"
                data-testid="deadlines-search-clear"
                aria-label="Clear"
              >×</button>
            )}
          </div>
          <Select value={fCategory} onValueChange={setFCategory}>
            <SelectTrigger className="w-44 h-10"><SelectValue placeholder={t("category")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {catalog.categories.map((c) => <SelectItem key={c} value={c}>{catLabels[c] || c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="w-44 h-10"><SelectValue placeholder={t("status")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {["expired", "critical", "warning", "ok", "no_term", "completed"].map((s) =>
                <SelectItem key={s} value={s}>{t(`status_${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={runReminders} data-testid={DEAD.runReminders} className="rounded-full btn-lift">
            <Bell className="w-4 h-4 mr-1.5" /> {t("run_reminders")}
          </Button>
          {isSuperAdmin && (
            <Button
              variant="outline"
              onClick={runBackfillGenerale}
              disabled={backfillRunning}
              data-testid="deadlines-backfill-generale"
              className="rounded-full btn-lift"
              title="Migra le scadenze Formazione Generale al catalogo corretto (senza scadenza) e riclassifica gli attestati combinati Generale+Specifica"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${backfillRunning ? "animate-spin" : ""}`} />
              {backfillRunning ? "Migrazione…" : "Migra Generali"}
            </Button>
          )}
          <Link to="/attestati">
            <Button variant="outline" data-testid="deadlines-import-attestati" className="rounded-full btn-lift">
              <ScanLine className="w-4 h-4 mr-1.5" />
              {lang === "it" ? "Da attestato/Excel" : "From certificate/Excel"}
            </Button>
          </Link>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditingId(null); setPendingFile(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid={DEAD.createBtn} className="rounded-full btn-lift" onClick={() => { setEditingId(null); setForm(EMPTY); }}>
                <Plus className="w-4 h-4 mr-1.5" /> {t("add_deadline")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader><DialogTitle>{editingId ? (lang === "it" ? "Modifica scadenza" : "Edit deadline") : t("add_deadline")}</DialogTitle></DialogHeader>
              <form onSubmit={submit} data-testid={DEAD.form} className="grid grid-cols-2 gap-4 max-h-[75vh] overflow-y-auto pr-1">
                <div>
                  <Label>{t("company")}</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v, worker_id: "" })}>
                    <SelectTrigger><SelectValue placeholder={t("company")} /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("worker")} <span className="text-muted-foreground text-[10px]">({t("optional")})</span></Label>
                  <Select value={form.worker_id || "none"} onValueChange={(v) => setForm({ ...form, worker_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {filteredFormWorkers.map((w) =>
                        <SelectItem key={w.id} value={w.id}>{w.last_name} {w.first_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("category")}</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, subtype: "", subtype_option_years: null })}>
                    <SelectTrigger data-testid={DEAD.category}><SelectValue /></SelectTrigger>
                    <SelectContent>{catalog.categories.map((c) => <SelectItem key={c} value={c}>{catLabels[c] || c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{lang === "it" ? "Tipo (voce catalogo)" : "Subtype"}</Label>
                  <Select value={form.subtype || "none"} onValueChange={(v) => setForm({ ...form, subtype: v === "none" ? "" : v, subtype_option_years: null })}>
                    <SelectTrigger data-testid="deadline-subtype-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {subtypesForCategory.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {form.subtype === "CORSO_FORMAZIONE_LAVORATORI_GENERALE" && (
                    <div className="mt-2 border border-emerald-200 bg-emerald-50 text-emerald-900 rounded-md p-2.5 text-xs" data-testid="deadline-generale-notice">
                      ℹ️ <b>Per legge questa scadenza NON ha termine</b> (Accordo Stato-Regioni 17/04/2025 — art. 37 D.Lgs. 81/08). Il campo "Scadenza" può restare vuoto.
                    </div>
                  )}
                </div>

                {hasOptions && (
                  <div className="col-span-2">
                    <Label>{lang === "it" ? "Validità" : "Validity"}</Label>
                    <Select value={String(form.subtype_option_years || currentSubtype.validity.options[0].years)} onValueChange={(v) => setForm({ ...form, subtype_option_years: Number(v), _prev_suggested: null })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {currentSubtype.validity.options.map((o) => <SelectItem key={o.years} value={String(o.years)}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label>{lang === "it" ? "Data formazione / redazione / analisi" : "Base date (course/document/analysis date)"}</Label>
                  <Input type="date" value={form.base_date} onChange={(e) => setForm({ ...form, base_date: e.target.value, _prev_suggested: null })} data-testid="deadline-base-date" />
                </div>
                <div>
                  <Label>{t("due_date")} <span className="text-muted-foreground text-[10px]">({lang === "it" ? "modificabile" : "editable"})</span></Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid={DEAD.due} />
                  {isManual && <p className="text-[11px] text-muted-foreground mt-1">{lang === "it" ? "Voce a scelta manuale: nessun calcolo automatico." : "Manual entry: no auto-compute."}</p>}
                </div>

                <div className="col-span-2"><Label>{t("title")}</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid={DEAD.title} /></div>
                <div className="col-span-2"><Label>{lang === "it" ? "Note" : "Notes"}</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="deadline-notes-input" /></div>

                <div className="col-span-2">
                  <Label>{lang === "it" ? "Attestato / documento (PDF, Word, JPG, PNG)" : "Attachment"}</Label>
                  <div className="flex gap-2 items-center mt-1">
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full btn-lift" data-testid="deadline-attach-button">
                      <Upload className="w-4 h-4 mr-1.5" /> {lang === "it" ? "Scegli file" : "Choose file"}
                    </Button>
                    <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" hidden onChange={(e) => setPendingFile(e.target.files?.[0] || null)} />
                    {pendingFile && <span className="text-xs text-muted-foreground truncate">{pendingFile.name}</span>}
                  </div>
                </div>

                <DialogFooter className="col-span-2">
                  <Button type="submit" data-testid={DEAD.submit} className="rounded-full btn-lift">{t("save")}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <ul className="space-y-2">
        {filtered.map((d) => {
          const days = daysBetween(d.due_date);
          const company = companies.find((c) => c.id === d.company_id);
          const worker = d.worker_id ? workers.find((w) => w.id === d.worker_id) : null;
          // Compute recipients preview: company primary + company.emails[] + worker.email — deduped, valid @
          const recipients = [];
          const addEmail = (e) => {
            const v = (e || "").trim();
            if (v && v.includes("@") && !recipients.includes(v)) recipients.push(v);
          };
          addEmail(company?.email);
          (company?.emails || []).forEach(addEmail);
          addEmail(worker?.email);
          const showRecipients = recipients.length > 0 && d.status !== "completed" && d.status !== "no_term";
          return (
            <li key={d.id} className={`bg-card border border-border rounded-lg px-5 py-4 flex items-center gap-4 severity-${d.status} card-hover flex-wrap`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate flex items-center gap-2">
                  {d.title}
                  {d.subtype_label && <span className="text-[10px] font-mono bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{d.subtype_label}</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {catLabels[d.category] || d.category} · {company?.name || "—"} · {d.due_date?.slice(0, 10) || t("status_no_term")}
                  {d.base_date && ` · ${lang === "it" ? "Dal" : "From"} ${d.base_date.slice(0,10)}`}
                </div>
                {d.notes && <div className="text-xs text-muted-foreground italic mt-1 line-clamp-2">{d.notes}</div>}
                {showRecipients && (
                  <div
                    className="text-[11px] text-muted-foreground mt-1.5 flex items-start gap-1.5 flex-wrap"
                    data-testid={`recipients-preview-${d.id}`}
                    title={recipients.join(", ")}
                  >
                    <Mail className="w-3 h-3 mt-0.5 shrink-0 opacity-70" />
                    <span className="shrink-0">{lang === "it" ? "Promemoria a:" : "Reminder to:"}</span>
                    {recipients.slice(0, 3).map((e) => (
                      <span key={e} className="bg-muted text-foreground/80 px-1.5 py-0.5 rounded font-mono">{e}</span>
                    ))}
                    {recipients.length > 3 && (
                      <span className="text-muted-foreground">+{recipients.length - 3} {lang === "it" ? "altri" : "more"}</span>
                    )}
                  </div>
                )}
              </div>
              {d.attachment_url && (
                <div className="flex items-center gap-2">
                  <a href={`${BACKEND}${d.attachment_url}`} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 text-primary hover:underline" data-testid={`view-attachment-${d.id}`}>
                    <FileText className="w-3.5 h-3.5" /> {lang === "it" ? "Apri" : "Open"}
                  </a>
                  <button onClick={() => downloadAttachment(d)} className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-primary btn-lift" data-testid={`download-attachment-${d.id}`}>
                    <Download className="w-3.5 h-3.5" /> {t("download")}
                  </button>
                  <button onClick={() => removeAttachment(d.id)} className="text-muted-foreground hover:text-destructive btn-lift" title={lang === "it" ? "Rimuovi allegato" : "Remove attachment"}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              {!d.attachment_url && (
                <>
                  <button onClick={() => attachRefs.current[d.id]?.click()} className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-primary btn-lift">
                    <Paperclip className="w-3.5 h-3.5" /> {lang === "it" ? "Allega" : "Attach"}
                  </button>
                  <input ref={(el) => (attachRefs.current[d.id] = el)} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" hidden onChange={(e) => e.target.files?.[0] && uploadAttachment(d.id, e.target.files[0])} />
                </>
              )}
              <div className={`text-xs px-2.5 py-1 rounded badge-${d.status}`}>
                {t(`status_${d.status}`)}{days != null && d.status !== "completed" && d.status !== "no_term" ? ` · ${days >= 0 ? days : -days} ${t("days_left")}` : ""}
              </div>
              {d.status !== "completed" && (
                <Button size="sm" variant="outline" onClick={() => complete(d.id)} className="rounded-full">
                  <Check className="w-3.5 h-3.5 mr-1" /> {t("mark_complete")}
                </Button>
              )}
              <button onClick={() => startEdit(d)} data-testid={`edit-deadline-${d.id}`} className="text-muted-foreground hover:text-primary btn-lift p-1" title={t("edit")}>
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => remove(d.id)} data-testid={`delete-deadline-${d.id}`} className="text-muted-foreground hover:text-destructive btn-lift p-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          );
        })}
        {filtered.length === 0 && <li className="text-sm text-muted-foreground">{t("no_data")}</li>}
      </ul>
    </div>
  );
}
